/* eslint-disable no-case-declarations */
import { useTranslation } from "react-i18next";
import { BreakdownTableDepths } from "@repo/utils";
import { useEstimatePresetStore, useEstimateStructure } from "@tools";

export const usePresetFieldNames = (usePresetTexts) => {
  const presetStore = useEstimatePresetStore();
  const structure = useEstimateStructure();
  const { t } = useTranslation();
  
  const getFieldName = (
    field,
    startingLevel = BreakdownTableDepths.SECTION,
    showNumerationColumn = false,
    usedDisplayName,
  ) => {
    const settings = structure?.settings;
    const selectedPresetData = presetStore?.selectedPresetData;
    
    let name = usePresetTexts
      ? selectedPresetData?.texts?.[field]
      : undefined;
    
    if (name)
      return name;
    
    if (usedDisplayName)
      return usedDisplayName;
    // if (typeof name !== "undefined") return name;

    switch (field) {
      case "pt-summary":
        return t("proposal_parts.editor_sections.summary");
      case "pt-timeline":
        return t("proposal_parts.editor_sections.timeline");
      // case "pt-timelineBeta":
      //   return t("proposal_parts.editor_sections.timeline_beta");
      case "pt-breakdown":
        return t("proposal_parts.editor_sections.breakdown");
      case "pt-textDescription":
        return t("proposal_parts.editor_sections.description");
      case "pt-timelineSections":
        return t("proposal_parts.timeline_dropdowns.sections");
      case "pt-timelineUtilization":
        return t("proposal_parts.timeline_dropdowns.utilization");
      case "pt-timelineBilling":
        return t("proposal_parts.timeline_dropdowns.billing");
      case "pt-totalCost":
        return t("views.editor.summary_table.totals.cost");
      case "pt-totalCompanyCost":
        return t("views.editor.summary_table.totals.company_cost");
      case "pt-totalRevenue":
        return t("views.editor.summary_table.totals.revenue");
      case "pt-totalProfit":
        return t("views.editor.summary_table.totals.profit");
      case "pt-totalProfitability":
        return t("views.editor.summary_table.totals.profitability");
      case "pt-totalTime":
        return t("views.editor.summary_table.totals.time");
      case "pt-totalTimeMonths":
        return t("times.month");
      case "pt-totalTimeWeeks":
        return  t("times.week");
      case "pt-totalHours":
        return t(
          `views.editor.summary_table.totals.${
            settings?.timeModifier === 1 ? "hours" : "days"
          }`
        );
      case "pt-totalEstimateHours":
        return t("times.h");
      case "pt-totalEstimateDays":
        return t("times.day");
      case "pt-work":
        return t("common.work_type");
      case "pt-rate":
        return t(
          `views.editor.summary_table.rate_${
            settings?.timeModifier === 1 ? "hours" : "days"
          }`
        );
      case "pt-cost":
        return t("views.editor.summary_table.cost");
        // return t("views.editor.summary_table.cost_currency", {
        //   currency: settings?.currencyObj.symbolStart || "$",
        // });
      case "pt-paCost":
        return t("views.editor.summary_table.cost_pa");
        // return t("views.editor.summary_table.cost_currency", {
        //   currency: settings?.currencyObj.symbolStart || "$",
        // });
      case "pt-revenue":
        return t("views.editor.summary_table.revenue");
      case "pt-price":
        return t("views.editor.price");
      case "pt-units":
        return t("views.editor.units");
      case "pt-unitPrice":
        return t("views.editor.unit_price");
      case "pt-combinedEstimate":
        return t(`views.editor.estimate.${settings?.timeModifier === 1 ? "hours" : "days"}`);
      case "pt-role":
        return t("views.editor.timeline_table.role");
      case "pt-min":
        return t("common.min");
      case "pt-max":
        return t("common.max");
      case "pt-billing":
        return t("views.editor.timeline_table.billing");
      case "pt-total":
        return t("common.total");
      case "pt-subtotal":
        return t("common.subtotal");
      case "pt-timeMta":
        return t(`views.editor.summary_table.time_${settings?.useWeekTime ? "weeks" : "months"}`);
      case "pt-profit":
        return t("views.editor.summary_table.profit");
      case "pt-profitability":
        return t("views.editor.summary_table.profitability");
      case "pt-hourlyCost":
        return t(
          `views.editor.summary_table.cost_${
            settings?.timeModifier === 1 ? "hours" : "days"
          }`
        );
      case "pt-teamMembers":
        return t("views.editor.summary_table.team_members");
      case "pt-time":
        return t(`times.${settings?.timeModifier === 1 ? "hour" : "day"}`);
      case "pt-module":
        const names = [];
        const viewLevel = typeof settings?.viewLevel === "number" ? settings?.viewLevel : 3;
        for(let i = startingLevel; i <= viewLevel; i++)
          names.push(t(`proposal_parts.module_levels.level_${i}`));
        return showNumerationColumn ? `${t("common.id")} / ${names.join(" / ")}` : names.join(" / ");
        // return t(
        //   `proposal_parts.${
        //     ["section", "module", "feature", "task"][startingLevel]
        //   }_title.${settings?.viewLevel || 3}`
        // );
      case "pt-desc":
        return t("views.editor.breakdown_table.columns.description");
      case "pt-desc2":
      case "pt-desc3":
      case "pt-desc4":
      case "pt-desc5":
        return document.querySelector(`.${field}`)?.textContent || t("views.editor.breakdown_table.columns.description");
      case "pt-labels":
        return t("views.editor.breakdown_table.columns.labels");
      case "pt-fixed":
        return t("views.editor.summary_table.fixed_costs");
      case "pt-showMoreContent":
        return t("common.see_more");
      case "pt-showLessContent":
        return t("common.see_less");
      case "pt-showPriceFor":
        return t("views.editor.price_simulation");
    }
  };

  return { getFieldName };
};
